import { useState, useEffect } from "react";
import Icon1 from "../icons/Icon";

const RangeDatePicker = ({
  value = { start_seconds: "", end_seconds: "" },
  mandatory = false,
  onChange,
  transparent = true,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [viewDate, setViewDate] = useState(new Date());
  const [nextViewDate, setNextViewDate] = useState(
    new Date(new Date().setMonth(viewDate.getMonth() + 1))
  );

  // Convertir les secondes en dates lors de l'initialisation et des mises à jour
  useEffect(() => {
    // Si les secondes sont fournies, convertissons-les en dates
    if (value.start_seconds) {
      const newStartDate = new Date(value.start_seconds * 1000);
      setStartDate(newStartDate);

      // Ajuster la vue du calendrier pour montrer le mois correspondant à la date de début
      if (newStartDate.getTime()) {
        setViewDate(new Date(newStartDate));

        const nextMonth = new Date(newStartDate);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        setNextViewDate(nextMonth);
      }
    } else {
      setStartDate(null);
    }

    if (value.end_seconds) {
      setEndDate(new Date(value.end_seconds * 1000));
    } else {
      setEndDate(null);
    }
  }, [value.start_seconds, value.end_seconds]);

  // Fonction pour convertir une date en secondes (timestamp Unix)
  const dateToSeconds = (date) => {
    if (!date) return "";
    // Diviser par 1000 pour obtenir des secondes à partir des millisecondes
    return Math.floor(date.getTime() / 1000);
  };

  const weekDays = {
    fr: ["lu", "ma", "me", "je", "ve", "sa", "di"],
  };

  const monthNames = {
    fr: [
      "janv.",
      "févr.",
      "mars",
      "avr.",
      "mai",
      "juin",
      "juil.",
      "août",
      "sept.",
      "oct.",
      "nov.",
      "déc.",
    ],
  };

  const formatDate = (date) => {
    if (!date) return "";
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
  };

  const toggleCalendar = () => {
    if (disabled) return; // Ne rien faire si le composant est désactivé
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Reset hover date when opening calendar
      setHoverDate(null);
    }
  };

  const navigateMonth = (direction, calendar) => {
    if (calendar === "first") {
      const newDate = new Date(viewDate);
      newDate.setMonth(newDate.getMonth() + direction);
      setViewDate(newDate);

      const newNextDate = new Date(newDate);
      newNextDate.setMonth(newNextDate.getMonth() + 1);
      setNextViewDate(newNextDate);
    } else if (calendar === "second") {
      const newDate = new Date(nextViewDate);
      newDate.setMonth(newDate.getMonth() + direction);
      setNextViewDate(newDate);
    } else if (calendar === "both") {
      // Pour la navigation d'année ou autre navigation globale
      const firstNewDate = new Date(viewDate);
      firstNewDate.setMonth(firstNewDate.getMonth() + direction);
      setViewDate(firstNewDate);

      const secondNewDate = new Date(nextViewDate);
      secondNewDate.setMonth(secondNewDate.getMonth() + direction);
      setNextViewDate(secondNewDate);
    }
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Adjust for Monday as first day of week (0 is Monday, 6 is Sunday)
    let firstDayOfWeek = firstDay.getDay() - 1;
    if (firstDayOfWeek === -1) firstDayOfWeek = 6;

    const days = [];

    // Add days from previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
      });
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    // Add days from next month
    const remainingCells = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= remainingCells; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const handleDateClick = (date) => {
    if (disabled) return;

    let newStartDate = startDate;
    let newEndDate = endDate;

    if (!startDate || (startDate && endDate) || date < startDate) {
      newStartDate = date;
      newEndDate = null;
      setStartDate(date);
      setEndDate(null);
    } else {
      newEndDate = date;
      setEndDate(date);
      // close
      setIsOpen(false);
      setHoverDate(null); // Reset hover state after selecting end date
    }

    // Appeler le onChange avec les valeurs en secondes
    if (onChange) {
      onChange({
        start_seconds: dateToSeconds(newStartDate),
        end_seconds: dateToSeconds(newEndDate),
      });
    }
  };

  const handleDateHover = (date) => {
    if (disabled) return;

    if (startDate && !endDate) {
      setHoverDate(date);
    }
  };

  const isDateInRange = (date) => {
    if (startDate && endDate) {
      return date > startDate && date < endDate;
    }
    return false;
  };

  const isDateInHoverRange = (date) => {
    if (startDate && hoverDate && !endDate) {
      // Only highlight the range if hover date is after start date
      if (hoverDate > startDate) {
        return date > startDate && date < hoverDate;
      }
    }
    return false;
  };

  const isHoverEndDate = (date) => {
    if (!startDate || endDate) return false;
    return (
      hoverDate &&
      date.toDateString() === hoverDate.toDateString() &&
      hoverDate > startDate
    );
  };

  const handleCalendarMouseLeave = () => {
    setHoverDate(null);
  };

  const inputValue =
    startDate && endDate
      ? `${formatDate(startDate)}  →  ${formatDate(endDate)}`
      : startDate
        ? `${formatDate(startDate)}  →  Date de fin`
        : "Date de début  →  Date de fin";

  return (
    <div className="relative w-full max-w-md ">
      <div
        className={`relative border rounded-xl p-2 flex items-center 
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-blue-300"} 
          ${isOpen ? "border-blue-300" : "border-gray-300"} 
          ${transparent ? "bg-transparent" : "bg-white"}`}
        onClick={toggleCalendar}
      >
        <input
          type="text"
          readOnly
          disabled={disabled}
          className={`w-full outline-none ${disabled ? "cursor-not-allowed" : "cursor-pointer"} text-h3 
            ${inputValue === "Date de début  →  Date de fin" ? "text-shadow_25" : "text-black"}`}
          value={inputValue}
        />
        <Icon1 size={20} icon_name="date" fill="grey"></Icon1>
        {mandatory && <span className="text-red-500 ml-1">*</span>}
      </div>

      {isOpen && !disabled && (
        <div
          className="absolute mt-1 p-2 bg-white border border-gray-200 rounded-md shadow-lg z-10"
          onMouseLeave={handleCalendarMouseLeave}
        >
          <div className="flex gap-4">
            {/* First Calendar */}
            <div className="w-64">
              <div className="flex justify-start items-center mb-2">
                <button
                  className="p-1 hover:bg-gray-100 rounded-full"
                  onClick={() => navigateMonth(-12, "both")}
                >
                  <Icon1 fill={"grey"} size={16} icon_name="doubleleft"></Icon1>
                </button>
                <button
                  className="p-1 hover:bg-gray-100 rounded-full"
                  onClick={() => navigateMonth(-1, "first")}
                >
                  <Icon1 fill={"grey"} size={16} icon_name="left"></Icon1>
                </button>
                <div className="font-medium text-h3 ml-10">
                  {monthNames.fr[viewDate.getMonth()]} {viewDate.getFullYear()}
                </div>{" "}
                <div></div>
              </div>

              <div className="grid grid-cols-7 text-center">
                {weekDays.fr.map((day) => (
                  <div
                    key={day}
                    className="text-xs font-medium text-gray-500 py-1"
                  >
                    {day}
                  </div>
                ))}

                {getMonthDays(viewDate).map((day, index) => (
                  <div
                    key={index}
                    className={`
                      h-6 w-6 mt-2 flex items-center justify-center text-sm rounded-md mx-auto
                      ${!day.isCurrentMonth ? "text-gray-300" : ""}
                      ${day.isCurrentMonth && day.date.toDateString() === new Date().toDateString() ? "border border-blue-400" : ""}
                      ${day.isCurrentMonth && startDate && day.date.toDateString() === startDate.toDateString() ? "bg-blue-500 text-white" : ""}
                      ${day.isCurrentMonth && endDate && day.date.toDateString() === endDate.toDateString() ? "bg-blue-500 text-white" : ""}
                      ${day.isCurrentMonth && isHoverEndDate(day.date) ? "bg-blue-300 text-white" : ""}
                      ${day.isCurrentMonth && isDateInRange(day.date) ? "bg-cyan-100" : ""}
                      ${day.isCurrentMonth && isDateInHoverRange(day.date) ? "bg-blue-50" : ""}
                      ${day.isCurrentMonth ? "hover:bg-gray-100 cursor-pointer" : ""}
                    `}
                    onClick={() =>
                      day.isCurrentMonth && handleDateClick(day.date)
                    }
                    onMouseEnter={() =>
                      day.isCurrentMonth && handleDateHover(day.date)
                    }
                  >
                    {day.date.getDate()}
                  </div>
                ))}
              </div>
            </div>

            {/* Second Calendar */}
            <div className="w-64">
              <div className="flex justify-end items-center mb-2">
                <div></div>
                <div className="font-medium text-h3 mr-10">
                  {monthNames.fr[nextViewDate.getMonth()]}{" "}
                  {nextViewDate.getFullYear()}
                </div>
                <button
                  className="p-1 hover:bg-gray-100 rounded-full"
                  onClick={() => navigateMonth(1, "both")}
                >
                  <Icon1 fill={"red"} size={16} icon_name="right"></Icon1>
                </button>{" "}
                <button
                  className="p-1 hover:bg-gray-100 rounded-full"
                  onClick={() => navigateMonth(12, "both")}
                >
                  <Icon1
                    fill={"grey"}
                    size={16}
                    icon_name="doubleright"
                  ></Icon1>
                </button>
              </div>

              <div className="grid grid-cols-7 text-center">
                {weekDays.fr.map((day) => (
                  <div
                    key={day}
                    className="text-xs font-medium text-gray-500 py-1"
                  >
                    {day}
                  </div>
                ))}

                {getMonthDays(nextViewDate).map((day, index) => (
                  <div
                    key={index}
                    className={`
                      h-6 w-6 mt-2 flex items-center justify-center text-sm rounded-md mx-auto
                      ${!day.isCurrentMonth ? "text-gray-300" : ""}
                      ${day.isCurrentMonth && day.date.toDateString() === new Date().toDateString() ? "border border-blue-400" : ""}
                      ${day.isCurrentMonth && startDate && day.date.toDateString() === startDate.toDateString() ? "bg-blue-500 text-white" : ""}
                      ${day.isCurrentMonth && endDate && day.date.toDateString() === endDate.toDateString() ? "bg-blue-500 text-white" : ""}
                      ${day.isCurrentMonth && isHoverEndDate(day.date) ? "bg-blue-300 text-white" : ""}
                      ${day.isCurrentMonth && isDateInRange(day.date) ? "bg-cyan-100" : ""}
                      ${day.isCurrentMonth && isDateInHoverRange(day.date) ? "bg-blue-50" : ""}
                      ${day.isCurrentMonth ? "hover:bg-gray-100 cursor-pointer" : ""}
                    `}
                    onClick={() =>
                      day.isCurrentMonth && handleDateClick(day.date)
                    }
                    onMouseEnter={() =>
                      day.isCurrentMonth && handleDateHover(day.date)
                    }
                  >
                    {day.date.getDate()}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RangeDatePicker;
